import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { fetchBySlug, getLocalized } from "../lib/cms";
import { SERVICES_BOXES, CONTACT_INFO } from "../data";
import Error404 from "./Error404";

// Map route slugs to CMS page slugs and gallery slugs
const SLUG_MAP = {
  "gulve": { pageSlug: "page-gulve", gallerySlug: "galleri-gulve" },
  "terrasser": { pageSlug: "page-terrasser", gallerySlug: "galleri-terrasser" },
  "vinduer-og-doere": { pageSlug: "vinduer-og-doere", gallerySlug: "vinduer-doere" },
  "udskiftning-af-vinduer": { pageSlug: "udskiftning-af-vinduer", gallerySlug: "vinduer-doere" },
  "koekken-montage": { pageSlug: "koekken-montage", gallerySlug: "koekkenmontage" },
  "tagarbejde-2": { pageSlug: "tagarbejde-2", gallerySlug: "tagarbejde" },
  "renovering-af-hus": { pageSlug: "renovering-af-hus", gallerySlug: "renovering-tilbygning" },
  "renovering": { pageSlug: "renovering", gallerySlug: "renovering-tilbygning" },
  "ombyg-renovering": { pageSlug: "ombyg-renovering", gallerySlug: "renovering-tilbygning" },
  "carport-skur": { pageSlug: "carport-skur", gallerySlug: "galleri-carport-skur" },
  "forsikringsskader": { pageSlug: "forsikringsskader", gallerySlug: "galleri-forsikringsskader" },
  "toemrermester": { pageSlug: "toemrermester", gallerySlug: null },
  "toemrerfirma": { pageSlug: "toemrerfirma", gallerySlug: null }
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [pageData, setPageData] = useState(null);
  const [galleryData, setGalleryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activeImageIdx, setActiveImageIdx] = useState(null); // Lightbox state

  const mapping = SLUG_MAP[slug] || { pageSlug: slug, gallerySlug: null };

  useEffect(() => {
    setLoading(true);
    setError(false);
    setPageData(null);
    setGalleryData(null);

    async function loadData() {
      try {
        // Fetch page content
        const page = await fetchBySlug(mapping.pageSlug);
        setPageData(page);

        // Fetch gallery if mapped
        if (mapping.gallerySlug) {
          try {
            const gallery = await fetchBySlug(mapping.gallerySlug);
            setGalleryData(gallery);
          } catch (gErr) {
            console.warn("Intet galleri fundet for denne ydelse:", gErr);
          }
        }
      } catch (err) {
        console.error("Fejl ved indlæsning af ydelse:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [slug]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-brand-brown border-t-transparent" />
      </div>
    );
  }

  if (error || !pageData) {
    return <Error404 />;
  }

  const title = getLocalized(pageData.title);
  const contentHtml = getLocalized(pageData.content);
  const metaTitle = getLocalized(pageData.meta_title) || `${title} - Bachmann Tømrer`;
  const metaDesc = getLocalized(pageData.meta_description) || `${title} udført af Bachmann Tømrer med fokus på kvalitet og solidt håndværk.`;

  // Filter related services to show as internal links
  const relatedServices = SERVICES_BOXES.filter((s) => s.href !== `/${slug}`).slice(0, 3);

  // Lightbox handlers
  const openLightbox = (idx) => setActiveImageIdx(idx);
  const closeLightbox = () => setActiveImageIdx(null);
  const nextImage = (e) => {
    e.stopPropagation();
    if (galleryData?.images) {
      setActiveImageIdx((prev) => (prev + 1) % galleryData.images.length);
    }
  };
  const prevImage = (e) => {
    e.stopPropagation();
    if (galleryData?.images) {
      setActiveImageIdx((prev) => (prev - 1 + galleryData.images.length) % galleryData.images.length);
    }
  };

  return (
    <div className="animate-in fade-in duration-500">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        {pageData.images?.[0] && <meta property="og:image" content={pageData.images[0]} />}
      </Helmet>

      {/* Page Hero */}
      <section className="relative py-24 overflow-hidden bg-brand-graphite text-white">
        {/* Background Image with Dark Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: `url(${pageData.images?.[0] || 'https://assets.auxodev.dk/assets/4308d28a-24e2-4c25-a37f-97d46b6f1512/image00038-scaled.jpeg'})`,
          }}
        />
        <div className="absolute inset-0 bg-brand-graphite/75" />
        
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center z-10">
          <span className="text-xs font-bold uppercase tracking-widest text-brand-beige">Bachmann Tømrer tilbyder</span>
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-white mt-2">
            {title}
          </h1>
          <div className="mt-4 flex items-center justify-center gap-2 text-xs text-white/70">
            <Link to="/" className="hover:text-brand-beige transition-colors">Forside</Link>
            <i className="fa-solid fa-chevron-right text-[8px]" />
            <span className="text-white font-medium">{title}</span>
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-3">
            {/* Left/Middle Column: Content & Gallery */}
            <div className="lg:col-span-2 space-y-12">
              {/* Rich Text Content */}
              <div 
                className="prose prose-brand max-w-none text-brand-graphite/80 leading-relaxed text-sm sm:text-base space-y-6"
                dangerouslySetInnerHTML={{ __html: contentHtml }}
              />

              {/* Connected Gallery Section */}
              {galleryData && galleryData.images && galleryData.images.length > 0 && (
                <div className="border-t border-brand-brown/10 pt-12">
                  <h2 className="font-serif text-2xl font-bold text-brand-graphite mb-2">
                    {getLocalized(galleryData.title)}
                  </h2>
                  <p className="text-xs text-brand-graphite/60 mb-6">
                    Se eksempler på vores arbejde med {title.toLowerCase()}. Klik på et billede for at se det i stor visning.
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                    {galleryData.images.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => openLightbox(idx)}
                        className="group relative aspect-square overflow-hidden rounded-xl border border-brand-brown/10 bg-brand-beige shadow-sm transition-all duration-300 hover:shadow-md hover:border-brand-brown/30 focus:outline-none"
                      >
                        <img
                          src={`${img}?width=400&quality=80&format=auto`}
                          alt={`${title} reference ${idx + 1}`}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-black/0 transition-colors duration-300 group-hover:bg-black/20 flex items-center justify-center">
                          <i className="fa-solid fa-magnifying-glass text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100 text-lg" />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column: Sidebar (Benefits, Related Services, Contact Card) */}
            <div className="space-y-8 lg:sticky lg:top-24 lg:max-h-[calc(100vh-8rem)] lg:overflow-y-auto pr-1 scrollbar-thin">
              {/* Advantages / Benefits Card */}
              <div className="rounded-2xl border border-brand-brown/10 bg-brand-beige/50 p-6 sm:p-8">
                <h3 className="font-serif text-lg font-bold text-brand-graphite mb-4 flex items-center gap-2">
                  <i className="fa-solid fa-circle-check text-brand-brown" /> Dine fordele hos os
                </h3>
                <ul className="space-y-3.5 text-xs text-brand-graphite/80">
                  <li className="flex items-start gap-2.5">
                    <i className="fa-solid fa-check text-brand-wood mt-0.5" />
                    <span><strong>14 års erfaring</strong> inden for tømrerfaget sikrer solid rådgivning og udførelse.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <i className="fa-solid fa-check text-brand-wood mt-0.5" />
                    <span><strong>Kvalitetsmaterialer</strong> der holder i mange år og sikrer et flot resultat.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <i className="fa-solid fa-check text-brand-wood mt-0.5" />
                    <span><strong>Tæt dialog</strong> og personlig service fra start til slut i projektet.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <i className="fa-solid fa-check text-brand-wood mt-0.5" />
                    <span><strong>Lokalt nærvær</strong> i Silkeborg, Ry, Kjellerup og hele Midt- og Østjylland.</span>
                  </li>
                </ul>
              </div>

              {/* Contact Card */}
              <div className="rounded-2xl bg-brand-brown p-6 sm:p-8 text-white shadow-lg">
                <h3 className="font-serif text-xl font-bold mb-3 text-white">
                  Skal vi give et tilbud?
                </h3>
                <p className="text-xs text-brand-beige/90 mb-6 leading-relaxed">
                  Har du et projekt eller en idé? Kontakt os i dag for at høre mere om, hvordan vi kan hjælpe dig.
                </p>
                <div className="space-y-3">
                  <Link
                    to="/kontakt"
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-white py-3 text-center text-xs font-bold uppercase tracking-wider text-brand-brown shadow-md transition-all duration-300 hover:bg-brand-beige"
                  >
                    <i className="fa-solid fa-paper-plane" /> Få et tilbud
                  </Link>
                  <a
                    href={`tel:${CONTACT_INFO.phoneRaw}`}
                    className="flex w-full items-center justify-center gap-2 rounded-full border border-white/30 bg-brand-brown-dark py-3 text-center text-xs font-bold uppercase tracking-wider text-white shadow-sm transition-all duration-300 hover:bg-brand-brown-dark/80"
                  >
                    <i className="fa-solid fa-phone" /> Ring nu
                  </a>
                </div>
              </div>

              {/* Related Services Links */}
              <div className="rounded-2xl border border-brand-brown/10 bg-white p-6 sm:p-8">
                <h3 className="font-serif text-base font-bold text-brand-graphite mb-4">
                  Andre ydelser
                </h3>
                <div className="space-y-3">
                  {relatedServices.map((service, idx) => (
                    <Link
                      key={idx}
                      to={service.href}
                      className="group flex items-center gap-3 rounded-xl border border-brand-brown/5 p-3 hover:border-brand-brown/20 hover:bg-brand-beige/30 transition-all duration-200"
                    >
                      <div className="h-10 w-10 shrink-0 overflow-hidden rounded-lg">
                        <img
                          src={`${service.image}?width=100&quality=80&format=auto`}
                          alt={service.title}
                          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-serif text-xs font-bold text-brand-graphite truncate group-hover:text-brand-brown transition-colors">
                          {service.title}
                        </h4>
                        <span className="text-[10px] text-brand-graphite/50 flex items-center gap-1">
                          Læs mere <i className="fa-solid fa-chevron-right text-[6px]" />
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {activeImageIdx !== null && galleryData && galleryData.images && (
        <div
          onClick={closeLightbox}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4 animate-in fade-in duration-300"
        >
          {/* Close button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:text-brand-wood text-3xl focus:outline-none z-50"
            aria-label="Luk galleri"
          >
            <i className="fa-solid fa-xmark" />
          </button>

          {/* Prev button */}
          <button
            onClick={prevImage}
            className="absolute left-4 text-white hover:text-brand-wood text-4xl focus:outline-none z-50 p-2"
            aria-label="Forrige billede"
          >
            <i className="fa-solid fa-chevron-left" />
          </button>

          {/* Image Container */}
          <div className="relative max-h-[85vh] max-w-[90vw] flex flex-col items-center justify-center">
            <img
              src={galleryData.images[activeImageIdx]}
              alt={`${title} reference stor`}
              className="max-h-[80vh] max-w-full object-contain rounded-lg shadow-2xl select-none"
              onClick={(e) => e.stopPropagation()}
            />
            <p className="text-white/80 text-xs mt-4 font-sans">
              Billede {activeImageIdx + 1} af {galleryData.images.length}
            </p>
          </div>

          {/* Next button */}
          <button
            onClick={nextImage}
            className="absolute right-4 text-white hover:text-brand-wood text-4xl focus:outline-none z-50 p-2"
            aria-label="Næste billede"
          >
            <i className="fa-solid fa-chevron-right" />
          </button>
        </div>
      )}
    </div>
  );
}
